import { Controller } from "@hotwired/stimulus"

// Polls the site's ingest_status endpoint while its GLOBAL indexing status is
// active (crawl + documents + external sources rolled up) and swaps each badge's
// server-rendered HTML in live, so the sub-statuses advance during a long run.
// When the global status goes terminal it reloads once (to refresh the rest of
// the page — chunk count, last-indexed — not just the badges), then stops. Does
// nothing for sites already terminal, so it never reload-loops.
//
// minVisible holds the spinner for a floor of N ms even when indexing finishes
// almost instantly (e.g. a docs-only reindex), so the user always gets visible
// feedback that something ran — without artificially delaying the actual job.
const ACTIVE = ["indexing", "crawling", "processing"]

export default class extends Controller {
  static targets = ["status", "site", "documents", "externalSources"]
  static values = {
    url: String,
    interval: { type: Number, default: 4000 },
    minVisible: { type: Number, default: 10000 }
  }

  connect() {
    const current = this.statusTarget.textContent.trim().toLowerCase()
    this.wasActive = ACTIVE.includes(current)
    this.startedAt = Date.now()
    if (this.wasActive) this.scheduleNext()
  }

  disconnect() {
    if (this.timer) clearTimeout(this.timer)
  }

  scheduleNext() {
    this.timer = setTimeout(() => this.poll(), this.intervalValue)
  }

  poll() {
    fetch(this.urlValue, { headers: { Accept: "application/json" } })
      .then((r) => r.json())
      .then((data) => {
        this.updateBadges(data.badges)
        if (ACTIVE.includes(data.status)) {
          this.scheduleNext()
        } else if (this.wasActive) {
          this.reloadAfterMinVisible()
        }
      })
      .catch(() => {
        this.timer = setTimeout(() => this.poll(), this.intervalValue * 2)
      })
  }

  // Swap in the server-rendered badge for each status we have a target for, so
  // variant colours + spinner stay owned by the Ruby helper.
  updateBadges(badges) {
    if (!badges) return
    const map = {
      status: this.hasStatusTarget && this.statusTarget,
      site: this.hasSiteTarget && this.siteTarget,
      documents: this.hasDocumentsTarget && this.documentsTarget,
      externalSources: this.hasExternalSourcesTarget && this.externalSourcesTarget
    }
    if (map.status && badges.status) map.status.innerHTML = badges.status
    if (map.site && badges.site) map.site.innerHTML = badges.site
    if (map.documents && badges.documents) map.documents.innerHTML = badges.documents
    if (map.externalSources && badges.external_sources) map.externalSources.innerHTML = badges.external_sources
  }

  reloadAfterMinVisible() {
    const remaining = this.minVisibleValue - (Date.now() - this.startedAt)
    this.timer = setTimeout(() => window.location.reload(), Math.max(0, remaining))
  }
}
